import {
	daysToMs,
	type FetchUserAgreementVersionActiveByAgreementTypeData,
	type FetchUserAgreementVersionActiveByAgreementTypeParams,
	sessionAtom,
	type UserAgreementVersion,
	userApi
} from '@capsicum/common';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

export type UseUserActiveAgreementQueryOptions = Omit<
	UseQueryOptions<FetchUserAgreementVersionActiveByAgreementTypeData, unknown, undefined | UserAgreementVersion>,
	'queryFn' | 'select'
>;
export const UseUserActiveAgreementQueryKey = ['user-active-agreement'];

export const useUserActiveAgreementQuery = (
	params: FetchUserAgreementVersionActiveByAgreementTypeParams,
	options?: Omit<UseUserActiveAgreementQueryOptions, 'queryFn' | 'queryKey'>
) => {
	const sessionTokens = useAtomValue(sessionAtom);

	return useQuery<FetchUserAgreementVersionActiveByAgreementTypeData, unknown, undefined | UserAgreementVersion>({
		enabled: !!sessionTokens?.authToken,
		queryFn: async () =>
			(await userApi).fetchUserAgreementVersionActiveByAgreementType({
				'details[]': ['agreementVersionTranslations'],
				...params
			}),
		queryKey: [UseUserActiveAgreementQueryKey, params],
		select: res => res.data,
		staleTime: daysToMs(1),
		...options
	});
};

export const UseUserAcceptAgreementQueryKey = ['user-accept-agreement'];

type UseUserAcceptAgreementQueryProps = {
	agreementVersionId: string;
};

export const useUserAcceptAgreementQuery = () => {
	const { data, error, isPending, isSuccess, mutateAsync } = useMutation({
		mutationFn: async ({ agreementVersionId }: UseUserAcceptAgreementQueryProps) =>
			(await userApi).postUserAgreementVersionAcceptByAgreementVersionId(agreementVersionId),
		mutationKey: UseUserAcceptAgreementQueryKey
	});

	return {
		data,
		error,
		isLoading: isPending,
		isSuccess,
		mutate: mutateAsync
	};
};
